import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { StringSchema, object as yupObject } from 'yup'
import { useFeatureFlags } from '@node-space/hooks'
import { Avatar, ComboboxItem, ComboboxProps } from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { useFormFields } from 'hooks/forms/useFormFields'
import { useYupFields } from 'hooks/forms/useYupFields'
import { useCountryStatesQuery } from 'hooks/queries/Customers/useCountryStates'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { CountryCodes, ErrorDetails } from 'types/types'
import { getFormattedSingleDateValue } from 'utils/date'
import { getCountryOptions, getFormObject } from 'utils/forms'
import {
  BASE_CUSTOMER_OPTIONS,
  FormKeyMapping,
  INDIVIDUAL_CURRENCIES,
  INDIVIDUAL_DESCRIPTION,
  INDIVIDUAL_EMPLOYMENT_STATUS,
  INDIVIDUAL_PEP_STATUS,
  INDIVIDUAL_SOURCE_OF_FUNDS,
  setFieldErrors,
} from '../helpers/form'
import { IndividualCustomerFormData, IndividualCustomerFormKey, UseCase } from '../helpers/types'
import { getIndividualCustomerErrorKey, getIndividualFieldLabel } from '../helpers/utils'

type OptionItemsMapping = FormKeyMapping<ComboboxItem[]>
type OptionStateMapping = FormKeyMapping<boolean>

export const useIndividualCustomerForm = (
  defaultValues: IndividualCustomerFormData,
  fieldErrors?: ErrorDetails
) => {
  const { t } = useTranslation()
  const { getYupField, getRequiredYupField, getEmailYupField, getConditionalYupField } =
    useYupFields()
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(defaultValues?.countryCode)

  const { enableOptionalEpcFields } = useFeatureFlags()
  const {
    data: countriesData = [],
    isFetching: isFetchingCountries,
    isError: isErrorFetchingCountries,
  } = useCountriesQuery()

  const {
    data: countryStates = [],
    isFetching: isFetchingCountryStates,
    isError: isErrorFetchingCountryStates,
  } = useCountryStatesQuery(selectedCountryCode, selectedCountryCode === CountryCodes?.US)

  const countryOptions = useMemo(() => getCountryOptions(countriesData), [countriesData])

  const getIndividualOptions = (options: Record<string, string>) =>
    Object.keys(options)?.map(key => ({
      value: options?.[key],
      leftText: t(`embeddedCustomers.individualOptions.${key}`),
    }))

  const comboboxItems: OptionItemsMapping = {
    ...BASE_CUSTOMER_OPTIONS,
    intendedUseOfAccount: getIndividualOptions(INDIVIDUAL_DESCRIPTION),
    nationality: countryOptions,
    countryCode: countryOptions,
    stateCode: countryStates,
    taxResidenceCountryCode: countryOptions,
    expectedMonthlyVolumeCurrency: INDIVIDUAL_CURRENCIES?.map(currency => ({
      LeftElement: <Avatar name={currency} size="small" />,
      leftText: currency,
      value: currency,
    })),
    employmentStatus: getIndividualOptions(INDIVIDUAL_EMPLOYMENT_STATUS),
    pepStatus: getIndividualOptions(INDIVIDUAL_PEP_STATUS),
    sourceOfFunds: getIndividualOptions(INDIVIDUAL_SOURCE_OF_FUNDS),
  }

  const isComboboxLoading: OptionStateMapping = {
    nationality: isFetchingCountries,
    countryCode: isFetchingCountries,
    stateCode: isFetchingCountryStates,
    taxResidenceCountryCode: isFetchingCountries,
  }

  const isErrorLoadingOptions: OptionStateMapping = {
    nationality: isErrorFetchingCountries,
    countryCode: isErrorFetchingCountries,
    stateCode: isErrorFetchingCountryStates,
    taxResidenceCountryCode: isErrorFetchingCountries,
  }

  const formKeys = Object.keys(defaultValues) as IndividualCustomerFormKey[]

  const getFieldSchema = (key: IndividualCustomerFormKey): StringSchema => {
    const label = getIndividualFieldLabel(key)
    switch (key) {
      case 'emailAddress':
        return getEmailYupField()
      case 'addressLine2':
      case 'externalReference':
        return getYupField({ label })
      case 'addressLine1':
      case 'city':
      case 'postalCode':
      case 'taxResidenceCountryCode':
      case 'countryCode':
        return enableOptionalEpcFields ? getYupField({ label }) : getRequiredYupField({ label })
      case 'stateCode':
        return getConditionalYupField<IndividualCustomerFormKey>({
          label,
          fieldKey: 'countryCode',
          fieldValue: CountryCodes?.US,
        })
      case 'description':
        return getRequiredYupField({
          label: t('embeddedCustomers.individualForm.customerDescription'),
        })
      default:
        return getRequiredYupField({ label })
    }
  }

  const formSchema = yupObject(getFormObject(formKeys, getFieldSchema))

  const form = useForm<IndividualCustomerFormData>({
    defaultValues: defaultValues,
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  })
  const { handleSubmit, setValue, setError, trigger, control } = form
  const formValues = useWatch({ control })
  const formErrors = form?.formState?.errors
  const hasErrors = !!Object.keys(formErrors)?.length
  const { setInputField, setNumberInputField, setComboboxField, setSingleDateField } =
    useFormFields<IndividualCustomerFormKey>(formSchema, form, formValues)

  const handleCountryCodeChange = (value: string) => {
    setSelectedCountryCode(value)
    formErrors?.stateCode && trigger('stateCode')
  }

  const getFormField = (key: IndividualCustomerFormKey) => {
    if (Object.keys(comboboxItems)?.includes(key)) {
      const comboboxField: ComboboxProps = {
        ...setComboboxField({
          key,
          items: comboboxItems?.[key],
          ...(key === 'countryCode' && { onChangeAdditional: handleCountryCodeChange }),
        }),
        loadingInline: isComboboxLoading?.[key],
        defaultValue: defaultValues?.[key],
      }
      return comboboxField
    }
    if (key === 'dateOfBirth') {
      return setSingleDateField(key)
    }
    if (key === 'expectedMonthlyVolumeAmount') {
      return setNumberInputField({ key })
    }
    return {
      ...setInputField({ key }),
      inputMode: 'text',
      onChange: (event: ChangeEvent<HTMLInputElement>) =>
        setValue(key, event?.target?.value, { shouldValidate: true, shouldDirty: true }),
    }
  }

  const formFields = getFormObject<IndividualCustomerFormKey, any>(formKeys, getFormField)

  const getComboboxLabel = <LabelType extends string>(key: IndividualCustomerFormKey) => {
    return comboboxItems?.[key]?.find(({ value }) => value === formValues?.[key])
      ?.leftText as LabelType
  }

  const displayData: Partial<IndividualCustomerFormData> = {
    dateOfBirth: getFormattedSingleDateValue(formValues?.dateOfBirth, DateFormat.DD_MMMM_YYYY),
    nationality: getComboboxLabel('nationality'),
    countryCode: getComboboxLabel('countryCode'),
    stateCode: getComboboxLabel('stateCode'),
    taxResidenceCountryCode: getComboboxLabel('taxResidenceCountryCode'),
    useCase: getComboboxLabel<UseCase>('useCase'),
  }

  const optionErrorFields = Object.keys(isErrorLoadingOptions)?.filter(
    key => isErrorLoadingOptions?.[key]
  )

  useEffect(() => {
    // Handle field errors from API
    if (fieldErrors) {
      setFieldErrors(fieldErrors, getIndividualCustomerErrorKey, setError)
    }
    if (optionErrorFields?.length) {
      optionErrorFields?.forEach(key => {
        setError(key as IndividualCustomerFormKey, {
          type: 'disabled',
          message: t('formFields.errors.loadingOptions'),
        })
      })
    }
  }, [optionErrorFields?.length, fieldErrors, setError, t])

  return { formFields, formValues, displayData, hasErrors, handleSubmit, form }
}
